import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * 404 page — simplified stage rig canvas behind the headline.
 * Renders a perspective floor grid + flying truss + 5 simulated beams.
 */
const NotFound: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(canvas);

    let raf = 0; let frame = 0;
    const tick = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      // Floor grid (perspective)
      const cx = w / 2, fy = h * 0.66;
      ctx.strokeStyle = "rgba(0, 170, 255, 0.18)";
      ctx.lineWidth = 1;
      for (let i = -8; i <= 8; i++) {
        ctx.beginPath();
        ctx.moveTo(cx, fy);
        ctx.lineTo(cx + i * 90, h);
        ctx.stroke();
      }
      for (let k = 1; k <= 6; k++) {
        const yy = fy + Math.pow(k / 6, 1.8) * (h - fy);
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(w, yy); ctx.stroke();
      }
      // Truss
      const trussY = h * 0.16;
      ctx.strokeStyle = "rgba(0, 170, 255, 0.55)";
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(cx - 220, trussY); ctx.lineTo(cx + 220, trussY); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx - 220, trussY + 14); ctx.lineTo(cx + 220, trussY + 14); ctx.stroke();
      for (let i = -4; i <= 4; i++) {
        ctx.beginPath();
        ctx.moveTo(cx + i * 55, trussY);
        ctx.lineTo(cx + i * 55 + 28, trussY + 14);
        ctx.stroke();
      }
      // Fixtures + beams
      const t = frame / 60;
      for (let i = -2; i <= 2; i++) {
        const fxX = cx + i * 90; const fxY = trussY + 22;
        ctx.fillStyle = "rgba(0,170,255,0.85)";
        ctx.beginPath(); ctx.arc(fxX, fxY, 4, 0, Math.PI * 2); ctx.fill();
        const sway = Math.sin(t + i) * 26;
        const gradient = ctx.createLinearGradient(fxX, fxY, cx + sway, fy);
        gradient.addColorStop(0, "rgba(0,170,255,0.55)");
        gradient.addColorStop(1, "rgba(0,170,255,0)");
        ctx.strokeStyle = gradient; ctx.lineWidth = 22;
        ctx.beginPath(); ctx.moveTo(fxX, fxY); ctx.lineTo(cx + sway, fy); ctx.stroke();
      }
      // Stage hot-spot
      const radial = ctx.createRadialGradient(cx, fy, 0, cx, fy, 180);
      radial.addColorStop(0, "rgba(0,170,255,0.25)");
      radial.addColorStop(1, "rgba(0,170,255,0)");
      ctx.fillStyle = radial;
      ctx.beginPath(); ctx.ellipse(cx, fy, 220, 60, 0, 0, Math.PI * 2); ctx.fill();

      frame++;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <main className="iav-page iav-404" data-testid="not-found-page">
      <canvas ref={canvasRef} className="iav-404-canvas" aria-hidden />
      <div className="iav-404-overlay" />
      <div className="iav-404-inner">
        <span className="iav-tag-line">Off Stage</span>
        <div className="iav-404-big">404</div>
        <h1 className="iav-404-h">SHOW'S NOT HERE</h1>
        <p className="iav-404-sub">Looks like this page went dark. Let's get you back on stage.</p>
        <div className="iav-cta-row iav-cta-center">
          <Link to="/" className="iav-btn iav-btn-primary" data-cursor="cta">Go Home</Link>
          <Link to="/gallery" className="iav-btn iav-btn-outline" data-cursor="cta">View Our Work</Link>
        </div>
      </div>
    </main>
  );
};
export default NotFound;
